import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import React, { useEffect } from 'react'
import { useToggle } from 'react-use'

import Icon, { IconName } from 'components/Icon'
import { NOTIFICATION_LEFT_PADDING } from 'components/navigation/Notifications/utils'
import theme from 'theme'

export const NotificationsListSection: React.FC<{
  sectionName: string
  sectionIcon: IconName | React.ReactNode
  sectionTitle: string
  sectionSubTitle?: string
  shouldCollapse?: boolean
  isLoading?: boolean
  hasError?: boolean
}> = ({
  sectionName,
  sectionIcon,
  sectionTitle,
  sectionSubTitle = '',
  shouldCollapse,
  isLoading = false,
  hasError = false,
  children
}) => {
  const [isOpen, setIsOpen] = useToggle(false)
  const handleOpenClose = () => {
    setIsOpen(!isOpen)
  }

  // collapse when parent tells us to
  useEffect(() => {
    if (shouldCollapse && isOpen) {
      setIsOpen(false)
    }
  }, [shouldCollapse, setIsOpen, isOpen])

  const icon =
    typeof sectionIcon === 'string' ? (
      <Icon
        css={{ color: theme.colors.steel[2] }} // TODO from figma '#193154'
        icon={sectionIcon}
      />
    ) : (
      sectionIcon
    )

  // Only display loading state if we haven't already received notifications
  // Otherwise, keep displaying the existing notifications
  if (isLoading && !children) {
    return (
      <ListItem component="div" data-test-notifications-loading={sectionName}>
        <ListItemIcon sx={{ '& > svg': { height: theme.space[4] } }}>
          {icon}
        </ListItemIcon>
        <ListItemText primary={sectionTitle} secondary="Loading..." />
      </ListItem>
    )
  }

  // Same goes for errors. react-query will keep retrying in the background
  // so just keep displaying the notifications we already have
  if (hasError && !children) {
    return (
      <ListItem component="div" data-test-notifications-error={sectionName}>
        <ListItemIcon sx={{ '& > svg': { height: theme.space[4] } }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          data-test-error-text
          primary={sectionTitle}
          secondary="Failed to load. Retrying..."
        />
      </ListItem>
    )
  }

  return (
    <>
      <ListItem
        component="div"
        data-test-notifications-section-button={sectionName}
      >
        <ListItemButton onClick={handleOpenClose}>
          <ListItemIcon
            sx={{
              '& > svg': { height: theme.space[4] },
              justifyContent: 'center'
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText primary={sectionTitle} secondary={sectionSubTitle} />
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <Icon icon={isOpen ? 'solidCaretUp' : 'solidCaretDown'} />
          </ListItemIcon>
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen} timeout="auto">
        <List disablePadding data-test-notifications-section={sectionName}>
          {children ? (
            children
          ) : (
            <ListItem component="div">
              <ListItemText
                data-test-notifications-section-empty
                secondary="No notifications"
                sx={{ pl: NOTIFICATION_LEFT_PADDING }}
              />
            </ListItem>
          )}
        </List>
      </Collapse>
    </>
  )
}
